// src/components/notifications/NotificationsBell.jsx
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from 'react';
import { createPortal } from 'react-dom';
import { useHistory } from 'react-router-dom';
import { openRadioNotification } from '../../pages/hitlab/radioParticipationIntent';
import { radioNotificationDestination } from '../../utils/radioParticipationUrl';
import axiosConn from '../../axiosConn';
import { SocketContext } from '../../context/socket';
import { AuthContext } from '../../context/AuthContext';
import { notificationText } from '../../utils/notificationText';
import {
  isUnauthorizedError,
  triggerAuthExpiredRecovery,
} from '../../utils/authSessionRecovery';

const BASE_URL = process.env.REACT_APP_BASE_URL || 'https://www.JoeGalaxy.NET';
const LIMIT = 20;

export default function NotificationsBell({ className = '' }) {
  const history = useHistory();
  const { player } = useContext(AuthContext);
  const { socket } = useContext(SocketContext);

  // Panel open state
  const [open, setOpen] = useState(false);

  // Count + items
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);

  // Paging
  const [mode, setMode] = useState('unread'); // 'unread' | 'all'
  const [cursor, setCursor] = useState(null); // { before, cursorId }
  const [hasMore, setHasMore] = useState(true);

  // Loading / error flags
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [authExpired, setAuthExpired] = useState(false);
  const [markAllPending, setMarkAllPending] = useState(false);
  const [markAllStatus, setMarkAllStatus] = useState('');

  // Refs
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const itemsRef = useRef([]);
  const loadingRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const markAllInFlightRef = useRef(false);
  const sentinelRef = useRef(null);

  // Responsive toggles
  const vw =
    typeof window !== 'undefined'
      ? window.visualViewport?.width ?? window.innerWidth ?? 1024
      : 1024;

  const showLabel = vw >= 768; // Desktop/tablet → show “Notifications”
  const smallPhone = vw <= 480; // Clamp dropdown to viewport on small phones

  // Icon map (defensive)
  const iconFor = useMemo(
    () => ({
      like: '❤️',
      comment: '💬',
      message: '📩',
      share: '🔗',
      system: '🔔',
      reward: '🏆',
      badge: '🎖️',
      clue: '🧩',
      checkin: '✅',
      hunt: '🚀',
      visit: '👋',
      jgkPaymentReceived: '🎉',
      adClick: '📣',
      galaxyJoinAccepted: '🌌',
      galaxyJoinRequest: '🛰️',
      referralReward: '💎',
    }),
    [],
  );

  // Helper: remove duplicates by _id while preserving order
  const dedupeById = useCallback((arr) => {
    const seen = new Set();
    const out = [];
    for (const it of arr) {
      const id = it?._id;
      if (!id) continue;
      if (seen.has(id)) continue;
      seen.add(id);
      out.push(it);
    }
    return out;
  }, []);

  // ---------- Helpers ----------
  const lastCursorFrom = (arr) => {
    const last = arr?.[arr.length - 1];
    if (!last) return null;
    return { before: last.createdAt, cursorId: last._id };
  };

  // ---------- Fetch unread count ----------
  const fetchUnreadCount = useCallback(async () => {
    try {
      const { data } = await axiosConn.get('notifications/my/unread-count', {
        headers: { Authorization: `Bearer ${player?.token}` },
      });
      const nextCount = Number(data?.data?.count || data?.count || 0);
      setCount(nextCount);
      return nextCount;
    } catch {
      /* noop */
      return null;
    }
  }, [player?.token]);

  // ---------- Fetch a page (with optional reset) ----------
  // ---------- Fetch a page (with optional reset + optional mode override) ----------
  const fetchList = useCallback(
    async ({ reset = false, modeOverride, preserveUnreadAfter } = {}) => {
      if (loadingRef.current) return;
      loadingRef.current = true;
      setError('');
      setAuthExpired(false);
      setLoading(reset);

      try {
        const effectiveMode = modeOverride ?? mode;
        const q = { limit: LIMIT };

        if (effectiveMode === 'unread') {
          q.status = 'unread';
          q.unreadOnly = true;
        }

        if (!reset && cursor?.before) q.before = cursor.before;
        if (!reset && cursor?.cursorId) q.cursor = cursor.cursorId;

        const { data } = await axiosConn.get('notifications/my', {
          params: q,
          headers: { Authorization: `Bearer ${player?.token}` },
        });

        const page = Array.isArray(data?.data || data?.notifications)
          ? data.data || data.notifications
          : [];

        // Ensure consistent order newest→oldest
        page.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        const currentItems = itemsRef.current || [];
        const preserveCutoff = preserveUnreadAfter
          ? new Date(preserveUnreadAfter).getTime()
          : null;
        const preservedUnread = Number.isFinite(preserveCutoff)
          ? currentItems.filter((item) => {
            if (!item || item.read) return false;
            const createdAt = new Date(item.createdAt).getTime();
            return Number.isFinite(createdAt) && createdAt > preserveCutoff;
          })
          : [];

        // Merge then de-duplicate by _id
        let nextItems = reset ? [...preservedUnread, ...page] : [...currentItems, ...page];
        nextItems = dedupeById(nextItems);

        setItems(nextItems);
        setHasMore(page.length === LIMIT);
        setCursor(lastCursorFrom(nextItems));
      } catch (e) {
        if (isUnauthorizedError(e)) {
          setAuthExpired(true);
          setError(
            'Your phone session expired. Sign in again to refresh notifications.',
          );
          triggerAuthExpiredRecovery({
            source: 'NotificationsBell',
            url: 'notifications/my',
          });
        } else {
          setError(e?.message || 'Failed to load notifications');
        }
      } finally {
        setLoading(false);
        loadingRef.current = false;
      }
    },
    [player?.token, mode, cursor, dedupeById],
  );

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // Defensive: if duplicates ever slip in, normalize state
  useEffect(() => {
    if (!items?.length) return;
    const unique = dedupeById(items);
    if (unique.length !== items.length) setItems(unique);
  }, [items, dedupeById]);

  // ---------- Positioning for portal panel ----------
  const [panelStyle, setPanelStyle] = useState({});
  const positionPanel = useCallback(() => {
    if (!btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const top = Math.max(8, r.bottom + 10);

    const GUTTER = 8;
    const MAX_W = 360;
    const DESKTOP_MIN_W = 260;
    const TINY_MIN_W = 220;

    if (smallPhone) {
      setPanelStyle({
        position: 'fixed',
        top,
        left: GUTTER,
        right: GUTTER,
        width: 'auto',
        minWidth: TINY_MIN_W,
        maxHeight: '75vh',
        overflow: 'auto',
        background: 'rgba(255,255,255,0.92)',
        border: '1px solid #e0e7ff',
        borderRadius: 18,
        boxShadow: '0 8px 32px rgba(60,60,120,0.18)',
        backdropFilter: 'blur(8px)',
        zIndex: 10000,
        display: 'grid',
        gridTemplateRows: 'auto 1fr auto',
        boxSizing: 'border-box',
        maxWidth: 'calc(100vw - ' + GUTTER * 2 + 'px)',
        overflowX: 'hidden',
      });
      return;
    }

    const vwNow = Math.max(
      document.documentElement.clientWidth || 0,
      window.innerWidth || 0,
    );
    const pw = Math.min(MAX_W, vwNow - GUTTER * 2);
    let left = Math.round(r.right - pw);
    left = Math.max(GUTTER, Math.min(left, vwNow - pw - GUTTER));

    setPanelStyle({
      position: 'fixed',
      top,
      left,
      width: pw,
      minWidth: DESKTOP_MIN_W,
      maxHeight: '80vh',
      overflow: 'auto',
      background: 'rgba(255,255,255,0.85)',
      border: '1px solid #e0e7ff',
      borderRadius: 18,
      boxShadow: '0 8px 32px rgba(60,60,120,0.18)',
      backdropFilter: 'blur(8px)',
      zIndex: 10000,
      display: 'grid',
      gridTemplateRows: 'auto 1fr auto',
      boxSizing: 'border-box',
      maxWidth: 'calc(100vw - ' + GUTTER * 2 + 'px)', // hard clamp to viewport
      overflowX: 'hidden',
    });
  }, [smallPhone]);

  // ---------- Live updates via socket ----------
  useEffect(() => {
    if (!socket || !socket.on) return;
    const onNew = (notif) => {
      setItems((prev) => {
        if (prev.some((n) => n._id === notif._id)) return prev;
        return [notif, ...prev].slice(0, 50);
      });
      if (!notif?.read) {
        setMarkAllStatus('');
        setCount((c) => c + 1);
      }
    };
    socket.on('notification:new', onNew);
    return () => socket.off && socket.off('notification:new', onNew);
  }, [socket]);

  // ---------- Poll unread count (backup) ----------
  useEffect(() => {
    fetchUnreadCount();
    const t = setInterval(fetchUnreadCount, 60_000);
    return () => clearInterval(t);
  }, [fetchUnreadCount]);

  // ---------- Open → position + first page ----------
  useLayoutEffect(() => {
    if (!open) return;

    // reset paging state
    setCursor(null);
    setHasMore(true);

    positionPanel();
    fetchList({ reset: true });

    const onReposition = () => positionPanel();
    window.addEventListener('resize', onReposition, { passive: true });
    window.addEventListener('scroll', onReposition, { passive: true });
    const vv = window.visualViewport;
    vv?.addEventListener?.('resize', onReposition, { passive: true });

    // microtask align
    const tm = setTimeout(positionPanel, 0);
    return () => {
      clearTimeout(tm);
      window.removeEventListener('resize', onReposition);
      window.removeEventListener('scroll', onReposition);
      vv?.removeEventListener?.('resize', onReposition);
    };
  }, [open, positionPanel, fetchList]);

  // ---------- Close on outside / ESC ----------
  useEffect(() => {
    function onDoc(e) {
      if (!open) return;
      if (
        panelRef.current?.contains(e.target) ||
        btnRef.current?.contains(e.target)
      )
        return;
      setOpen(false);
    }
    function onEsc(e) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('click', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('click', onDoc);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  // ---------- When mode changes, reset & refetch with the NEW mode ----------
  useEffect(() => {
    if (!open) return;
    setCursor(null);
    setHasMore(true);
    fetchList({ reset: true, modeOverride: mode });
  }, [mode, open, fetchList]);

  // ---------- Infinite scroll inside the panel ----------
  useEffect(() => {
    if (!open) return;
    const rootEl = panelRef.current;
    const target = sentinelRef.current;
    if (!rootEl || !target) return;

    const io = new IntersectionObserver(
      (entries) => {
        const vis = entries[0]?.isIntersecting;
        if (vis && hasMore && !loadingRef.current && !loadingMoreRef.current) {
          loadingMoreRef.current = true;
          fetchList({ reset: false }).finally(() => {
            loadingMoreRef.current = false;
          });
        }
      },
      { root: rootEl, rootMargin: '200px', threshold: 0 },
    );

    io.observe(target);
    return () => io.disconnect();
  }, [open, hasMore, fetchList]);

  // ---------- Actions ----------
  async function markOneRead(id, returnTo) {
    try {
      setItems((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n)),
      );
      setCount((c) => Math.max(0, c - 1));
      await axiosConn.patch(
        `notifications/${id}/read`,
        {},
        {
          headers: { Authorization: `Bearer ${player?.token}` }, // use token, no withCredentials
          ...(returnTo ? { skipAuthExpiredRecovery: true } : {}),
        },
      );
      // If filtering unread, remove the item from view
      if (mode === 'unread') {
        setItems((prev) => prev.filter((n) => n._id !== id));
        if (items.filter((n) => !n.read && n._id !== id).length < LIMIT) {
          // backfill if we're running low
          fetchList({ reset: false });
        }
      }
    } catch (error) {
      if (returnTo && isUnauthorizedError(error)) triggerAuthExpiredRecovery({ source: 'Radio notification', returnTo });
      /* best-effort */
    }
  }

  async function markAllRead() {
    const hasUnread = count > 0 || itemsRef.current.some((n) => !n.read);
    if (authExpired || markAllInFlightRef.current || markAllPending || !hasUnread) return;

    markAllInFlightRef.current = true;
    setMarkAllPending(true);
    setMarkAllStatus('');
    setError('');

    try {
      const { data } = await axiosConn.patch(
        'notifications/my/read-all',
        {},
        {
          headers: { Authorization: `Bearer ${player?.token}` },
        },
      );
      const readAt = data?.data?.readAt || new Date().toISOString();

      setCursor(null);
      setHasMore(true);
      const [remainingUnread] = await Promise.all([
        fetchUnreadCount(),
        fetchList({
          reset: true,
          modeOverride: mode,
          preserveUnreadAfter: readAt,
        }),
      ]);
      setMarkAllStatus(
        remainingUnread > 0
          ? 'Marked earlier notifications read. New notifications are still unread.'
          : 'All notifications marked read.',
      );
    } catch (e) {
      if (isUnauthorizedError(e)) {
        setAuthExpired(true);
        setError(
          'Your phone session expired. Sign in again to refresh notifications.',
        );
        triggerAuthExpiredRecovery({
          source: 'NotificationsBell',
          url: 'notifications/my/read-all',
        });
      } else {
        setError(e?.message || 'Could not mark notifications read.');
      }
    } finally {
      markAllInFlightRef.current = false;
      setMarkAllPending(false);
    }
  }

  function openNotification(n) {
    const url = n?.cta?.url || BASE_URL;
    markOneRead(n._id, n.type === 'hitlabRadioRotationDrop' ? radioNotificationDestination(url) : undefined);
    if (n.type === 'hitlabRadioRotationDrop') {
      if (openRadioNotification(url, history)) {
        setOpen(false);
        return;
      }
      return; // Invalid Radio targets never fall through to unrestricted navigation.
    }
    window.location.href = url;
  }

  // ---------- Button visual (desktop label; mobile icon) ----------
  const hasCount = count > 0;
  const hasUnread = count > 0 || items.some((n) => !n.read);
  const markAllDisabled = authExpired || markAllPending || !hasUnread;

  return (
    <div className={className} style={{ position: 'relative' }}>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications (${count} unread)`}
        style={{
          position: 'relative',
          border: '1px solid #e5e7eb',
          borderRadius: 999,
          background: '#fff',
          padding: showLabel ? '8px 44px 8px 14px' : '6px 36px 6px 12px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: showLabel ? 10 : 6,
          cursor: 'pointer',
          fontWeight: 700,
          height: showLabel ? 36 : 32,
          minWidth: showLabel ? 148 : 46,
          whiteSpace: 'nowrap',
          boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
          overflow: 'visible', // allow badge outside
        }}
      >
        <span role="img" aria-hidden>
          🔔
        </span>
        {showLabel && (
          <span
            style={{
              fontSize: 14,
              color: '#374151',
              fontWeight: 700,
              letterSpacing: 0.2,
              whiteSpace: 'nowrap',
            }}
          >
            Notifications
          </span>
        )}

        {hasCount && (
          <span
            style={{
              position: 'absolute',
              top: showLabel ? -10 : -6, // raise up
              right: showLabel ? -12 : -4, // push further right
              minWidth: 24,
              height: 20,
              padding: '0 7px',
              borderRadius: 999,
              background: '#ef4444',
              color: '#fff',
              fontSize: 12.5,
              lineHeight: '20px',
              textAlign: 'center',
              fontWeight: 800,
              boxShadow: '0 0 0 2px #fff, 0 2px 6px rgba(0,0,0,0.15)',
              pointerEvents: 'none',
              zIndex: 2,
            }}
            aria-label={`${count} unread`}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {/* Panel (portal) */}
      {open &&
        createPortal(
          <div
            ref={panelRef}
            role="dialog"
            aria-label="Notifications"
            style={panelStyle}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 10,
                flexWrap: smallPhone ? 'wrap' : 'nowrap',
                padding: 16,
                borderBottom: '1px solid #e0e7ff',
                background: 'linear-gradient(90deg, #facc15 0%, #a7f3d0 100%)',
                borderTopLeftRadius: 18,
                borderTopRightRadius: 18,
              }}
            >
              <div
                style={{
                  fontWeight: 900,
                  fontSize: 18,
                  background: 'linear-gradient(90deg, #f59e42, #6366f1)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  letterSpacing: 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  minWidth: 0,
                }}
              >
                🔔 Notifications
              </div>

              {/* Unread / All toggle (simple & non-breaking) */}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  gap: 8,
                  flex: '1 1 auto',
                  flexWrap: smallPhone ? 'wrap' : 'nowrap',
                  minWidth: 0,
                }}
              >
                <button
                  type="button"
                  onClick={() => setMode('unread')}
                  aria-pressed={mode === 'unread'}
                  style={{
                    fontSize: 12,
                    padding: '6px 10px',
                    borderRadius: 999,
                    border:
                      mode === 'unread'
                        ? '1px solid #6366f1'
                        : '1px solid #e5e7eb',
                    background: mode === 'unread' ? '#fff' : '#f3f4f6',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow:
                      mode === 'unread'
                        ? '0 0 0 2px rgba(99,102,241,0.16)'
                        : 'none',
                  }}
                >
                  Unread
                </button>

                <button
                  type="button"
                  onClick={() => setMode('all')}
                  aria-pressed={mode === 'all'}
                  style={{
                    fontSize: 12,
                    padding: '6px 10px',
                    borderRadius: 999,
                    border:
                      mode === 'all'
                        ? '1px solid #6366f1'
                        : '1px solid #e5e7eb',
                    background: mode === 'all' ? '#fff' : '#f3f4f6',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow:
                      mode === 'all'
                        ? '0 0 0 2px rgba(99,102,241,0.16)'
                        : 'none',
                  }}
                >
                  All
                </button>

                <button
                  type="button"
                  onClick={async () => {
                    // Manual refresh of current mode
                    setCursor(null);
                    setHasMore(true);
                    await fetchList({ reset: true });
                  }}
                  style={{
                    fontSize: 16,
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: '#6366f1',
                    color: '#fff',
                    cursor: 'pointer',
                    boxShadow: '0 2px 8px #6366f133',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  title="Refresh"
                >
                  ↻
                </button>

                <button
                  type="button"
                  onClick={markAllRead}
                  disabled={markAllDisabled}
                  style={{
                    fontSize: 13,
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: 8,
                    background:
                      markAllDisabled
                        ? '#cbd5e1'
                        : 'linear-gradient(90deg, #6366f1 0%, #60a5fa 100%)',
                    color: '#fff',
                    cursor: markAllDisabled ? 'not-allowed' : 'pointer',
                    fontWeight: 800,
                    boxShadow: '0 2px 8px #6366f133',
                    opacity: markAllDisabled ? 0.68 : 1,
                  }}
                >
                  {markAllPending ? 'Marking...' : 'Mark all read'}
                </button>
              </div>
            </div>

            {/* List */}
            <div>
              {markAllStatus && !error && (
                <div
                  aria-live="polite"
                  style={{ padding: '10px 16px', fontSize: 13, color: '#047857' }}
                >
                  {markAllStatus}
                </div>
              )}
              {loading && (
                <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>
                  Loading…
                </div>
              )}
              {error && (
                <div style={{ padding: 16, fontSize: 13, color: '#b91c1c' }}>
                  {error}
                  {authExpired && (
                    <div style={{ marginTop: 10 }}>
                      <button
                        type="button"
                        onClick={() => {
                          window.location.href = '/login';
                        }}
                        style={{
                          border: 0,
                          borderRadius: 999,
                          background: '#111827',
                          color: '#fff',
                          cursor: 'pointer',
                          fontWeight: 800,
                          padding: '8px 12px',
                        }}
                      >
                        Sign in again
                      </button>
                    </div>
                  )}
                </div>
              )}
              {!loading && !error && !authExpired && !items.length && (
                <div style={{ padding: 16, fontSize: 13, color: '#6b7280' }}>
                  You’re all caught up ✨
                </div>
              )}

              {items.map((n) => {
                const title = notificationText(n.title) || 'Notification';
                const message = notificationText(n.message);
                return (
                <div
                  key={n._id}
                  onClick={() => openNotification(n)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) =>
                    (e.key === 'Enter' || e.key === ' ') && openNotification(n)
                  }
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '32px 1fr auto',
                    gap: 12,
                    padding: '14px 16px',
                    borderTop: '1px solid #e0e7ff',
                    background: n.read
                      ? 'linear-gradient(90deg, #f3f4f6 0%, #e0e7ff 100%)'
                      : 'linear-gradient(90deg, #fef9c3 0%, #a7f3d0 100%)',
                    cursor: 'pointer',
                    borderLeft: n.read
                      ? '4px solid #e0e7ff'
                      : '4px solid #facc15',
                    borderRadius: 12,
                    margin: '6px 0',
                    alignItems: 'center',
                  }}
                >
                  <div style={{ fontSize: 22 }}>{iconFor[n.type] || '🔔'}</div>
                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: n.read ? 600 : 900,
                        fontSize: 15,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        color: n.read ? '#6366f1' : '#b45309',
                      }}
                    >
                      {title}
                    </div>
                    {message && (
                      <div
                        style={{
                          color: '#6b7280',
                          fontSize: 13,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {message}
                      </div>
                    )}
                  </div>
                  <div
                    style={{ display: 'grid', gap: 6, alignContent: 'center' }}
                  >
                    {n?.cta?.label && (
                      <div
                        onClick={(e) => {
                          e.stopPropagation();
                          openNotification(n);
                        }}
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          padding: '4px 10px',
                          border: 'none',
                          borderRadius: 8,
                          background: '#6366f1',
                          color: '#fff',
                          textAlign: 'center',
                          boxShadow: '0 1px 4px #6366f133',
                          cursor: 'pointer',
                        }}
                      >
                        {n.cta.label}
                      </div>
                    )}
                    {!n.read && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          markOneRead(n._id);
                        }}
                        style={{
                          fontSize: 12,
                          color: '#fff',
                          background:
                            'linear-gradient(90deg, #6366f1 0%, #60a5fa 100%)',
                          border: 0,
                          borderRadius: 6,
                          padding: '2px 10px',
                          cursor: 'pointer',
                          fontWeight: 700,
                          boxShadow: '0 1px 4px #6366f133',
                        }}
                      >
                        Mark read
                      </button>
                    )}
                  </div>
                </div>
                );
              })}

              {/* Infinite scroll sentinel */}
              {hasMore && <div ref={sentinelRef} style={{ height: 1 }} />}
            </div>

            {/* Footer */}
            <div
              style={{
                padding: 10,
                borderTop: '1px solid #f3f4f6',
                fontSize: 12,
                color: '#6b7280',
              }}
            >
              Showing {items.length} • New items appear in real time
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
